import defaultAvatar from '@hotjar/shared-assets/images/default-avatar.png';

import type { User, UserDTO, UserId } from '@hotjar/user';
import { dtoToModel as userDtoToUserModel } from '@hotjar/user';

import type {
  Note as NoteDto,
  Comment as CommentDto,
  Thread as ThreadDto,
  Reply as ReplyDto,
  CommentContent,
  RecordingSpecificFields,
  ResponseSpecificFields,
} from './dto';
import type {
  Thread,
  Comment,
  CommentId,
  WidgetId,
  ContentToken,
  MentionToken,
  Reply,
  EmptyObject,
} from './model';

const ANONYMOUS_USER_NAME = 'Anonymous';

function getUser(users: UserDtoMap, userId: UserId): User {
  const userDto = users[userId];
  if (!userDto) {
    return {
      id: userId,
      name: ANONYMOUS_USER_NAME,
      avatar: defaultAvatar,
      email: null,
    };
  }
  return userDtoToUserModel({ ...userDto, id: userId });
}

type UserDtoMap = { [key: number]: Omit<UserDTO, 'id'> };

export function tokenModelToTokenDto(content: Array<ContentToken>): CommentContent {
  return content.map((token) => {
    switch (token.type) {
      case 'mention':
        return {
          type: token.type,
          value: token.value.id,
        };
      default:
        return token;
    }
  });
}

function tokenDtoToTokenModel(
  // eslint-disable-next-line @typescript-eslint/default-param-last
  content: CommentContent = [],
  users: UserDtoMap,
): Array<ContentToken> {
  return content
    .map((token) => {
      switch (token.type) {
        case 'mention': {
          return {
            type: token.type,
            value: getUser(users, token.value),
          };
        }
        default:
          return token;
      }
    })
    .filter((token): token is ContentToken => token !== null);
}

export function commentModelToNoteDto(comment: Comment): NoteDto {
  return {
    id: comment.id,
    created_epoch_time: comment.createdEpochTime,
    note: comment.content
      .map((token) => {
        switch (token.type) {
          case 'mention': {
            return token.value.name;
          }
          default:
            return token.value;
        }
      })
      .join(''),
    user_name: comment.author.name,
    user_id: comment.author.id,
    user_picture_url: comment.author.avatar || '',
    // when note is updated, the whole entity must be sent
    // to the server, including time; metadata it's a property of the Thread
    // but changing the type here and in all places above would break
    // compatibility with API v2 which is simply not worth it, as v1 module
    // is going to be retired soon. I hope so.
    ...(comment.metadata &&
      'time' in comment.metadata && { time: comment.metadata.time as number }),
  };
}

export function threadModelToNoteDto(thread: Thread): NoteDto {
  return {
    ...commentModelToNoteDto(thread),
    ...('time' in thread.metadata ? { time: thread.metadata.time as number } : {}),
  };
}

export function noteDtoToThreadModel(widgetId: WidgetId, note: NoteDto): Thread {
  return {
    id: note.id,
    widgetId,
    metadata: { time: note.time ?? -1 },
    author: {
      id: note.user_id,
      name: note.user_name || ANONYMOUS_USER_NAME,
      email: null,
      avatar: note.user_picture_url,
    },
    createdEpochTime: note.created_epoch_time,
    content: [
      {
        type: 'text',
        value: note.note,
      },
    ],
    replies: [],
  };
}

export function commentDtoToCommentModel(users: UserDtoMap, comment: CommentDto): Comment {
  return {
    id: comment.id,
    author: getUser(users, comment.user_id),
    createdEpochTime: comment.created_epoch_time,
    content: tokenDtoToTokenModel(comment.content, users),
  };
}

export function replyDtoToReplyModel(
  users: UserDtoMap,
  reply: ReplyDto,
  threadId: CommentId,
): Reply {
  return {
    ...commentDtoToCommentModel(users, reply),
    threadId,
  };
}

function subtypeScpecificFieldsToModel(thread: ThreadDto): {
  widgetId: number;
  metadata: EmptyObject;
} {
  if ('recording_id' in thread) {
    return { widgetId: thread.recording_id, metadata: { time: thread.time } };
  }

  if ('response_id' in thread) {
    return { widgetId: thread.response_id, metadata: {} };
  }

  // NOTE: the generic thread doesn't have Widget ID as there is no connection
  // to the widget in it! It's the widget what holds reference to the comment
  return { widgetId: -1, metadata: {} };
}

export function threadDtoToThreadModel(users: UserDtoMap, thread: ThreadDto): Thread {
  return {
    ...commentDtoToCommentModel(users, thread),
    ...subtypeScpecificFieldsToModel(thread),
    replies: thread.thread.map((reply) => replyDtoToReplyModel(users, reply, thread.id)),
  };
}

export function commentModelToCommentDto(comment: Comment): CommentDto {
  return {
    id: comment.id,
    user_id: comment.author.id,
    created_epoch_time: comment.createdEpochTime,
    content: tokenModelToTokenDto(comment.content),
    mentioned_user_ids: comment.content
      .filter((token): token is MentionToken => token.type === 'mention')
      .map((mentionToken) => mentionToken.value.id),
  };
}

export function replyModelToReplyDto(reply: Reply): ReplyDto {
  return {
    ...commentModelToCommentDto(reply),
    thread_id: reply.threadId,
  };
}

function subtypeSpecificFieldsToDto(
  thread: Thread,
): RecordingSpecificFields | ResponseSpecificFields {
  if ('time' in thread.metadata) {
    return { recording_id: thread.widgetId, time: thread.metadata.time as number };
  }

  return { response_id: thread.widgetId };
}

export function threadModelToThreadDto(thread: Thread): ThreadDto {
  return {
    ...commentModelToCommentDto(thread),
    ...subtypeSpecificFieldsToDto(thread),
    thread: thread.replies ? thread.replies.map(replyModelToReplyDto) : [],
  };
}
